// client/src/components/imports/ImportsPage.jsx
//
// /:tenant/imports (Roadmap Phase 5.3), owners and admins:
//   - Members from a CSV: choose the file, match its columns, check for
//     duplicates (choose create / update / skip per row), import. Undoable.
//   - Eventbrite: connect, pick past events, import them with their
//     attendees (as contacts) in the background.
//   - Scheduled posts from a CSV: Phase 3's bulk scheduler.
//   - History of every import.
//
// API: /api/imports/:tenant (server/routes/importRoutes.js).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaFileCsv, FaCalendarAlt, FaUndo } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import LoadingState from '../common/LoadingState';
import {
  getImportsOverview, memberTemplateUrl, previewMembers, checkMembers, commitMembers, getImport, undoImport,
  listEventbriteEvents, startEventbriteImport, eventbriteLoginUrl, disconnectEventbrite, errorText,
} from '../../services/importsApi';
import '../publishing/Publishing.css';

function apiOrigin() {
  try { return new URL(process.env.REACT_APP_BASE_URL || window.location.origin).origin; } catch { return window.location.origin; }
}
/** Open the Eventbrite sign-in popup; resolves when it reports back or is closed. */
function connectPopup(url, type) {
  return new Promise((resolve) => {
    const w = window.open(url, `${type}-connect`, 'width=560,height=700');
    let finished = false;
    const finish = (ok) => {
      if (finished) return;
      finished = true;
      window.removeEventListener('message', onMessage);
      clearInterval(timer);
      resolve(ok);
    };
    const onMessage = (e) => {
      if (e.origin !== apiOrigin() && e.origin !== window.location.origin) return;
      if (e.data && e.data.type === type) finish(Boolean(e.data.ok));
    };
    window.addEventListener('message', onMessage);
    const timer = setInterval(() => { if (!w || w.closed) finish(null); }, 700);
  });
}

function day(value) {
  if (!value) return '';
  const s = String(value);
  const d = new Date(s.includes('T') ? s : `${s.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}
const today = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------- members CSV

function MembersImport({ tenant, onDone, t }) {
  const [file, setFile] = useState(null);
  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [check, setCheck] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [consent, setConsent] = useState(false);
  const [tag, setTag] = useState(`import ${today()}`);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => { setFile(null); setCsv(''); setPreview(null); setCheck(null); setDecisions({}); setResult(null); setError(''); };
  const pick = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    reset();
    setBusy('preview');
    try {
      const text = await f.text();
      const p = await previewMembers(tenant, text);
      setFile(f.name); setCsv(text); setPreview(p); setMapping(p.mapping || {});
    } catch (err) { setError(errorText(err, t('imports.members.readError', 'The file could not be read.'))); }
    setBusy('');
  };
  const runCheck = async () => {
    setBusy('check'); setError('');
    try {
      const r = await checkMembers(tenant, csv, mapping);
      setCheck(r);
      setDecisions({});
    } catch (err) { setError(errorText(err, t('imports.members.checkError', 'The file could not be checked.'))); }
    setBusy('');
  };
  const runImport = async () => {
    setBusy('import'); setError('');
    try {
      const r = await commitMembers(tenant, { csv, mapping, decisions, fileName: file, consent, tag: tag.trim() || null });
      setResult(r.totals);
      onDone();
    } catch (err) { setError(errorText(err, t('imports.members.importError', 'The import didn’t finish. Nothing was lost; check the history and try again.'))); }
    setBusy('');
  };

  const statusLabel = {
    new: t('imports.status.new', 'New'), existing: t('imports.status.existing', 'Already a member'), possible: t('imports.status.possible', 'Possible duplicate'),
    repeat: t('imports.status.repeat', 'Repeated in file'), invalid: t('imports.status.invalid', 'Can’t import'),
  };
  const actionLabel = { create: t('imports.action.create', 'Add'), update: t('imports.action.update', 'Update'), skip: t('imports.action.skip', 'Skip') };
  const toAdd = useMemo(() => {
    if (!check) return 0;
    return check.rows.filter((r) => (decisions[r.row] || r.defaultAction) !== 'skip').length + (check.truncated ? check.total - check.rows.length : 0);
  }, [check, decisions]);
  const reviewRows = check ? check.rows.filter((r) => r.status !== 'new') : [];

  return (
    <section className="surface" data-testid="members-import">
      <h3 style={{ marginTop: 0 }}><FaFileCsv aria-hidden="true" /> {t('imports.members.title', 'Members from a spreadsheet')}</h3>
      <p className="pb-ai-meta">
        {t('imports.members.help', 'Export your member list as a CSV file (from Excel, Google Sheets or your old system). You’ll match its columns, check for duplicates, then import. New members aren’t emailed.')}{' '}
        <a href={memberTemplateUrl(tenant)}>{t('imports.members.template', 'Download a sample file')}</a>
      </p>
      {result ? (
        <div className="note ok" role="status" data-testid="members-result">
          {t('imports.members.done', 'Done: {{created}} added, {{updated}} updated, {{skipped}} skipped, {{contacts}} added as contacts only, {{failed}} failed.', result)}
          {' '}<button type="button" className="pb-btn" onClick={reset}>{t('imports.members.another', 'Import another file')}</button>
        </div>
      ) : (
        <>
          <label className="pb-field" htmlFor="im-file">
            {t('imports.members.file', 'CSV file')}
            <input id="im-file" type="file" accept=".csv,text/csv" onChange={pick} />
          </label>
          {busy === 'preview' && <LoadingState />}
          {error && <div className="note error" role="alert">{error}</div>}

          {preview && (
            <>
              <p className="pb-summary">{t('imports.members.rows', '{{count}} people in {{file}}', { count: preview.rowCount, file })}</p>
              <div className="pb-form" data-testid="mapping">
                {preview.fields.map((f) => (
                  <label key={f.key} className="pb-field" htmlFor={`im-map-${f.key}`}>
                    {t(`imports.field.${f.key}`, f.label)}{f.required ? ' *' : ''}
                    <select id={`im-map-${f.key}`} value={mapping[f.key] ?? ''} onChange={(e) => { setMapping((m) => ({ ...m, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) })); setCheck(null); }}>
                      <option value="">{t('imports.members.notInFile', '— not in the file —')}</option>
                      {preview.headers.map((h, i) => <option key={`${h}-${i}`} value={i}>{h || t('imports.members.column', 'Column {{n}}', { n: i + 1 })}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              <div className="pb-table-wrap">
                <table className="pb-table">
                  <thead><tr>{preview.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                  <tbody>{preview.rows.map((r, i) => <tr key={i}>{preview.headers.map((_, j) => <td key={j}>{r[j]}</td>)}</tr>)}</tbody>
                </table>
              </div>
              {!check && (
                <div className="pb-actions">
                  <button type="button" className="pb-btn primary" onClick={runCheck} disabled={busy === 'check' || mapping.email === undefined}>
                    {t('imports.members.check', 'Check for duplicates')}
                  </button>
                  {mapping.email === undefined && <span className="pb-ai-meta">{t('imports.members.needEmail', 'Choose the email column first.')}</span>}
                </div>
              )}
            </>
          )}

          {check && (
            <div data-testid="check">
              <p className="pb-summary">
                {t('imports.members.counts', '{{new}} new · {{existing}} already members · {{possible}} possible duplicates · {{repeat}} repeated · {{invalid}} can’t be imported', check.counts)}
              </p>
              {reviewRows.length > 0 && (
                <div className="pb-table-wrap">
                  <table className="pb-table">
                    <thead><tr>
                      <th>{t('imports.members.row', 'Row')}</th><th>{t('imports.members.person', 'Person')}</th>
                      <th>{t('imports.members.found', 'What we found')}</th><th>{t('imports.members.do', 'Do')}</th>
                    </tr></thead>
                    <tbody>
                      {reviewRows.map((r) => (
                        <tr key={r.row} data-testid="review-row">
                          <td>{r.row}</td>
                          <td>{r.name}<br /><span className="pb-ai-meta">{r.email}</span></td>
                          <td>{statusLabel[r.status]}{r.message && <><br /><span className="pb-ai-meta">{r.message}</span></>}{r.status === 'existing' && r.match && <><br /><span className="pb-ai-meta">{r.match.name}</span></>}</td>
                          <td>
                            {r.actions.length > 1 ? (
                              <select aria-label={t('imports.members.actionFor', 'What to do with row {{n}}', { n: r.row })} value={decisions[r.row] || r.defaultAction}
                                onChange={(e) => setDecisions((d) => ({ ...d, [r.row]: e.target.value }))}>
                                {r.actions.map((a) => <option key={a} value={a}>{actionLabel[a]}</option>)}
                              </select>
                            ) : actionLabel.skip}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="pb-form">
                {mapping.email_opt_in !== undefined && (
                  <label className="pb-checks pb-wide">
                    <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                    {' '}{t('imports.members.consent', 'People marked “yes” in the email column agreed to receive email from us')}
                  </label>
                )}
                <label className="pb-field" htmlFor="im-tag">
                  {t('imports.members.tag', 'Tag everyone imported (for audience segments)')}
                  <input id="im-tag" value={tag} maxLength={64} onChange={(e) => setTag(e.target.value)} />
                </label>
              </div>
              {error && <div className="note error" role="alert">{error}</div>}
              <div className="pb-actions">
                <button type="button" className="pb-btn primary" onClick={runImport} disabled={busy === 'import' || toAdd === 0}>
                  {busy === 'import' ? t('imports.members.importing', 'Importing…') : t('imports.members.import', 'Import {{count}} people', { count: toAdd })}
                </button>
                <button type="button" className="pb-btn" onClick={() => setCheck(null)}>{t('imports.members.back', 'Change columns')}</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- Eventbrite

function EventbriteImport({ tenant, status, onChanged, t }) {
  const [list, setList] = useState(null);
  const [picked, setPicked] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (opts = {}) => {
    setError('');
    try {
      const r = await listEventbriteEvents(tenant, opts);
      setList((prev) => (opts.continuation && prev ? { ...r, events: [...prev.events, ...r.events] } : r));
    } catch (err) { setError(errorText(err, t('imports.eventbrite.loadError', 'Eventbrite events could not be loaded.'))); }
  }, [tenant, t]);

  useEffect(() => { if (status.connected) load(); }, [status.connected, load]);

  if (!status.configured) {
    return (
      <section className="surface" data-testid="eventbrite">
        <h3 style={{ marginTop: 0 }}><FaCalendarAlt aria-hidden="true" /> {t('imports.eventbrite.title', 'Events from Eventbrite')}</h3>
        <p className="pb-ai-meta">{t('imports.eventbrite.notSetUp', 'Eventbrite import isn’t set up on this server yet.')}</p>
      </section>
    );
  }
  const connect = async () => {
    const ok = await connectPopup(eventbriteLoginUrl(tenant), 'EVENTBRITE_CONNECTED');
    if (ok === false) setError(t('imports.eventbrite.connectError', 'Eventbrite didn’t connect. Try again.'));
    onChanged();
  };
  const disconnect = async () => {
    if (!window.confirm(t('imports.eventbrite.confirmDisconnect', 'Disconnect Eventbrite? Events already imported stay.'))) return;
    await disconnectEventbrite(tenant).catch(() => {});
    setList(null);
    onChanged();
  };
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const start = async () => {
    setBusy(true); setError('');
    try {
      await startEventbriteImport(tenant, { orgId: list.orgId, eventIds: picked });
      setNotice(t('imports.eventbrite.started', 'Import started. It runs in the background and appears in the history below; large events take a few minutes.'));
      setPicked([]);
      onChanged();
    } catch (err) { setError(errorText(err, t('imports.eventbrite.startError', 'The import couldn’t start.'))); }
    setBusy(false);
  };

  return (
    <section className="surface" data-testid="eventbrite">
      <div className="pb-actions" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}><FaCalendarAlt aria-hidden="true" /> {t('imports.eventbrite.title', 'Events from Eventbrite')}</h3>
        {status.connected && <button type="button" className="pb-btn" onClick={disconnect}>{t('imports.eventbrite.disconnect', 'Disconnect')}</button>}
      </div>
      <p className="pb-ai-meta">{t('imports.eventbrite.help', 'Bring in your past Eventbrite events and their attendees. Each event becomes a past event here (private), and attendees become contacts tagged with the event’s name, so you can invite them back. Running it again never adds anything twice.')}</p>
      {!status.connected && <button type="button" className="pb-btn primary" onClick={connect}>{t('imports.eventbrite.connect', 'Connect Eventbrite')}</button>}
      {error && <div className="note error" role="alert">{error}</div>}
      {notice && <div className="note ok" role="status">{notice}</div>}
      {status.connected && !list && !error && <LoadingState />}
      {list && (
        <>
          {list.organizations.length > 1 && (
            <label className="pb-field" htmlFor="eb-org">
              {t('imports.eventbrite.org', 'Organization')}
              <select id="eb-org" value={list.orgId} onChange={(e) => { setPicked([]); load({ orgId: e.target.value }); }}>
                {list.organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          )}
          {list.events.length === 0 && <p className="pb-ai-meta">{t('imports.eventbrite.none', 'No past events in this Eventbrite organization.')}</p>}
          <div className="pb-checks" data-testid="eventbrite-events">
            {list.events.map((e) => (
              <label key={e.id}>
                <input type="checkbox" disabled={e.imported} checked={picked.includes(e.id)} onChange={() => toggle(e.id)} />
                {' '}{e.name}{e.start && ` · ${day(e.start.local)}`}{e.imported && ` (${t('imports.eventbrite.imported', 'already imported')})`}
              </label>
            ))}
          </div>
          <div className="pb-actions">
            {list.continuation && <button type="button" className="pb-btn" onClick={() => load({ orgId: list.orgId, continuation: list.continuation })}>{t('imports.eventbrite.more', 'Show older events')}</button>}
            <button type="button" className="pb-btn primary" onClick={start} disabled={busy || picked.length === 0}>
              {t('imports.eventbrite.import', 'Import {{count}} events', { count: picked.length })}
            </button>
          </div>
        </>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- page

export default function ImportsPage({ tenantId }) {
  const params = useParams();
  const tenant = tenantId || params.tenant;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  const load = useCallback(() => getImportsOverview(tenant).then(setData).catch((err) => {
    if (err?.response?.status === 403) setDenied(true);
    else setError(errorText(err, t('imports.loadError', 'Imports could not be loaded.')));
  }), [tenant, t]);
  useEffect(() => { load(); }, [load]);

  // Refresh while an Eventbrite import is running.
  const running = data && data.imports.some((i) => i.status === 'running');
  useEffect(() => {
    if (!running) return undefined;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [running, load]);

  const undo = async (imp) => {
    if (!window.confirm(t('imports.history.confirmUndo', 'Undo this import? Members it added are removed (unless they have since signed in) and members it updated get their old details back. Contacts stay.'))) return;
    try {
      const r = await undoImport(tenant, imp.id);
      setError('');
      window.alert(t('imports.history.undone', 'Undone: {{removed}} removed, {{restored}} restored, {{kept}} kept.', { removed: r.removed || 0, restored: r.restored || 0, kept: r.kept || 0 }));
      load();
    } catch (err) { setError(errorText(err, t('imports.history.undoError', 'The import couldn’t be undone.'))); }
  };
  const view = async (imp) => {
    if (open && open.id === imp.id) { setOpen(null); return; }
    try { setOpen((await getImport(tenant, imp.id)).import); } catch (err) { setError(errorText(err, t('imports.loadError', 'Imports could not be loaded.'))); }
  };

  const buttons = [{ icon: <FaArrowLeft />, label: t('common.back', 'Back'), onClick: () => navigate(-1) }];
  const statusLabel = {
    running: t('imports.history.running', 'Running'), done: t('imports.history.done', 'Done'), failed: t('imports.history.failed', 'Failed'),
    undone: t('imports.history.undoneStatus', 'Undone'), interrupted: t('imports.history.interrupted', 'Stopped'),
  };
  const summary = (i) => (i.kind === 'members'
    ? t('imports.history.membersSummary', '{{created}} added · {{updated}} updated · {{skipped}} skipped', { created: i.totals.created || 0, updated: i.totals.updated || 0, skipped: i.totals.skipped || 0 })
    : t('imports.history.eventbriteSummary', '{{events}} events · {{attendees}} attendees ({{contacts}} new contacts)', { events: i.totals.events || 0, attendees: i.totals.attendees || 0, contacts: i.totals.contacts || 0 }));

  return (
    <div className="pb-page">
      <DesktopActionBar buttons={buttons} />
      <section className="surface">
        <h2>{t('imports.title', 'Imports')}</h2>
        <p className="surface-muted">{t('imports.intro', 'Moving to On-Screen? Bring in your members, your past Eventbrite events and attendees, and a calendar of posts.')}</p>
      </section>

      {!data && !error && !denied && <LoadingState />}
      {denied && <div className="note info" role="status">{t('imports.denied', 'Owners and admins run imports.')}</div>}
      {error && <div className="note error" role="alert">{error}</div>}
      {data && !data.ready && <div className="note warn" role="status">{t('imports.migration', 'Imports need a database update before they can be used.')}</div>}

      {data && data.ready && (
        <>
          <MembersImport tenant={tenant} onDone={load} t={t} />
          <EventbriteImport tenant={tenant} status={data.eventbrite} onChanged={load} t={t} />
          <section className="surface">
            <h3 style={{ marginTop: 0 }}>{t('imports.posts.title', 'Scheduled posts from a spreadsheet')}</h3>
            <p className="pb-ai-meta">{t('imports.posts.help', 'Plan many posts at once from a CSV: date, time, platforms, text and media links.')}</p>
            <button type="button" className="pb-btn" onClick={() => navigate(`/${tenant}/media/bulk-schedule`)}>{t('imports.posts.open', 'Open bulk scheduling')}</button>
          </section>

          <section className="surface" data-testid="history">
            <h3 style={{ marginTop: 0 }}>{t('imports.history.title', 'Import history')}</h3>
            {data.imports.length === 0 && <p className="pb-ai-meta">{t('imports.history.empty', 'Nothing imported yet.')}</p>}
            {data.imports.length > 0 && (
              <div className="pb-table-wrap">
                <table className="pb-table">
                  <thead><tr>
                    <th>{t('imports.history.when', 'When')}</th><th>{t('imports.history.what', 'What')}</th>
                    <th>{t('imports.history.result', 'Result')}</th><th>{t('imports.history.status', 'Status')}</th><th>{t('imports.history.actions', 'Actions')}</th>
                  </tr></thead>
                  <tbody>
                    {data.imports.map((i) => (
                      <tr key={i.id} data-testid="history-row">
                        <td>{day(i.createdOn)}</td>
                        <td>{i.kind === 'members' ? (i.fileName || t('imports.history.members', 'Members')) : `Eventbrite${i.source ? `: ${i.source}` : ''}`}</td>
                        <td>{summary(i)}{i.message && <><br /><span className="pb-ai-meta">{i.message}</span></>}</td>
                        <td>{statusLabel[i.status] || i.status}</td>
                        <td>
                          <div className="pb-actions">
                            {i.kind === 'members' && <button type="button" className="pb-btn" onClick={() => view(i)}>{t('imports.history.view', 'Details')}</button>}
                            {i.kind === 'members' && i.status === 'done' && <button type="button" className="pb-btn" onClick={() => undo(i)}><FaUndo aria-hidden="true" /> {t('imports.history.undo', 'Undo')}</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {open && (
              <div className="pb-preview" data-testid="import-rows">
                <strong>{open.fileName}</strong>
                <ul className="pb-msgs">
                  {open.rows.filter((r) => ['failed', 'contact', 'skipped'].includes(r.action)).slice(0, 200).map((r) => (
                    <li key={r.row} className={r.action === 'failed' ? 'err' : 'warn'}>
                      {t('imports.history.rowLine', 'Row {{row}}', { row: r.row })} · {r.email || '—'} · {r.message || r.action}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </>
      )}
      <AppFooter buttons={buttons} />
    </div>
  );
}
